import React, { useEffect, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { Controller } from "react-hook-form";

interface IProps {
  name: string
  error?: any
  label?: string
  control: any
  isRequired?: boolean
  disabled?: boolean
  type?: string
  isClearable?: boolean
  loadOptions: (inputValue?: string) => Promise<any>
  reloadOnRender?: boolean
}

const AsyncSelectField = React.forwardRef<HTMLInputElement, IProps>((props: IProps, ref) => {
  const {
    name,
    error,
    label,
    control,
    isClearable,
    isRequired = false,
    loadOptions,
    disabled = false,
  } = props;
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)

  const inputRef = useRef<HTMLInputElement>();

  function fetchData(inputValue?: string) {
    setLoading(true)

    if (inputValue) {
      loadOptions(inputValue).then((response: []) => {
        setOptions(response)
        setLoading(false)
      })
    } else {
      loadOptions().then((response: []) => {
        setOptions(response)
        setLoading(false)
      })
    }
  }

  useEffect(() => {
    if (props.reloadOnRender)
      fetchData()
  }, []);

  // Fetch after changing the input
  useEffect(() => {
    if (open) {
      fetchData(inputValue)
    }
  }, [open, inputValue])

  function onOpen() {
    setInputValue('')

    setTimeout(() => {
      setOpen(true)
    });
  }

  function onClose() {
    setOpen(false)
  }

  return (
    <FormControl fullWidth>
      <Controller
        render={({ field }) => (
          <Autocomplete
            ref={ref}
            open={open}
            onOpen={() => onOpen()}
            onClose={() => onClose()}
            value={field.value || null}
            disableClearable={loading || !isClearable}
            loading={loading}
            filterOptions={(x) => x}
            disabled={disabled}
            getOptionLabel={(option) => option.label}
            options={options}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => <TextField
              inputRef={inputRef}
              {...params}
              required={isRequired}
              label={label}
              error={!!error}
              helperText={error ? error.message : null}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              inputProps={{
                ...params.inputProps, ...{
                  'data-testid': field.name
                }
              }}
            />}
            onChange={(_event, value) => {
              field.onChange(value);
            }}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
            }}
          />
        )}
        name={name}
        control={control}
      />
    </FormControl>
  );
})

AsyncSelectField.defaultProps = {
  isRequired: false,
  disabled: false,
};

export { AsyncSelectField };